/**
 * MockDraftCompleteModal - [Phase 2a.2-B §4.2]
 *
 * Surfaced when backend broadcasts MOCK_DRAFT_COMPLETE on a mock-draft
 * league. Commissioner chooses:
 *   - End Mock      → POST /api/leagues/{id}/end-mock         (always shown)
 *   - Convert to Season → POST /api/leagues/{id}/convert-to-season
 *                        (shown only when league_type == MOCK_DRAFT_MOCK_SEASON)
 *
 * Non-commissioners see the modal in read-only form (no buttons).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { authHeaders } from "../../contexts/AuthContext";
import { Trophy, Archive, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MockDraftCompleteModal({
  leagueId, leagueType, isCommissioner, onClose,
}) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(null); // "end" | "convert" | null
  const [error, setError] = useState(null);
  const [teamCount, setTeamCount] = useState(null);

  const canConvert = leagueType === "MOCK_DRAFT_MOCK_SEASON";

  // [HF2-10 v1.2] Fetch league team count so we can pre-disable the Convert
  // button when fewer than 2 active teams exist (founder hit this with a
  // 1-team Mock and got an opaque "Failed to convert" message). Backend also
  // validates as defense-in-depth.
  useEffect(() => {
    if (!canConvert) return;
    // [HF2-10 v1.2] GET /api/leagues/{id} returns the `managers` array
    // (each entry is a LeagueTeam joined with User). Count active managers.
    axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() })
      .then((r) => {
        const list = r.data?.managers || [];
        setTeamCount(list.length);
      })
      .catch(() => setTeamCount(null));
  }, [leagueId, canConvert]);

  const enoughTeams = teamCount == null ? true : teamCount >= 2;

  const endMock = async () => {
    setLoading("end"); setError(null);
    try {
      await axios.post(`${API}/leagues/${leagueId}/end-mock`, {}, { headers: authHeaders() });
      nav("/dashboard");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to end mock");
      setLoading(null);
    }
  };

  const convertToSeason = async () => {
    setLoading("convert"); setError(null);
    try {
      await axios.post(`${API}/leagues/${leagueId}/convert-to-season`, {}, { headers: authHeaders() });
      nav(`/league/${leagueId}/home`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to convert");
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="mock-draft-complete-modal">
      <div className="bg-[#1C1C1C] border border-white/[0.10] rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <Trophy size={18} className="text-[#FF6B00]" />
          <p className="font-display font-bold text-xs uppercase tracking-widest text-[#FF6B00]">Mock Draft Complete</p>
        </div>
        <h2 className="font-display font-black text-2xl uppercase text-[#F5F5F5] leading-tight mb-3">
          What's next?
        </h2>

        {!isCommissioner ? (
          <p className="font-body text-sm text-[rgba(255,255,255,0.65)] mb-2">
            Waiting on the commissioner to choose what happens next.
          </p>
        ) : (
          <>
            <div className="space-y-3 mb-5">
              <div className="border border-white/[0.10] rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Archive size={18} className="text-[rgba(255,255,255,0.65)] mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm uppercase text-[#F5F5F5]">End Mock</p>
                    <p className="font-body text-xs text-[rgba(255,255,255,0.55)] mt-0.5">
                      Archive this league. Rosters frozen as a record. No season plays out.
                    </p>
                  </div>
                </div>
              </div>
              {canConvert && (
                <div className={`border rounded-xl p-4 ${enoughTeams ? "border-[#FF6B00]/30 bg-[#FF6B00]/[0.04]" : "border-white/[0.10] bg-white/[0.02] opacity-70"}`}>
                  <div className="flex items-start gap-3">
                    <ChevronRight size={18} className={enoughTeams ? "text-[#FF6B00] mt-0.5" : "text-[rgba(255,255,255,0.35)] mt-0.5"} />
                    <div className="flex-1 min-w-0">
                      <p className={`font-display font-bold text-sm uppercase ${enoughTeams ? "text-[#FF6B00]" : "text-[rgba(255,255,255,0.55)]"}`}>Convert to Season</p>
                      <p className="font-body text-xs text-[rgba(255,255,255,0.65)] mt-0.5">
                        Promote this draft into a Mock Season. The bridge runs, schedules generate, Week 1 begins.
                      </p>
                      {!enoughTeams && (
                        <p
                          className="font-body text-xs text-[#FF8533] mt-1.5"
                          data-testid="convert-min-teams-warning"
                        >
                          At least 2 teams required to convert. Invite more managers or add CPUs from the lobby.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <p className="font-body text-xs text-[#FF4444] mb-3" data-testid="mock-modal-error">{error}</p>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={endMock}
                disabled={!!loading}
                data-testid="end-mock-btn"
                className="px-5 py-2.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.10] font-display font-bold text-sm uppercase text-[#F5F5F5] transition-all disabled:opacity-50"
              >
                {loading === "end" ? "Ending..." : "End Mock"}
              </button>
              {canConvert && (
                <button
                  onClick={convertToSeason}
                  disabled={!!loading || !enoughTeams}
                  data-testid="convert-to-season-btn"
                  className="px-5 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] font-display font-black text-sm uppercase text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading === "convert" ? "Converting..." : "Convert to Season"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
